var React = require('react');
var { useRef, useState, useEffect } = React;

function useNodeDrag(opts) {
  var panPosRef = opts.panPosRef;
  var zoomRef = opts.zoomRef;
  var setWorkflowState = opts.setWorkflowState;
  var workflowStateRef = opts.workflowStateRef;
  var measureNodes = opts.measureNodes;
  var nodesRef = opts.nodesRef;
  var selectedNodeIdsRef = opts.selectedNodeIdsRef;
  var getRectRef = opts.getRectRef;
  var onLongPressSelect = opts.onLongPressSelect;
  var onNodeTap = opts.onNodeTap;
  var onTouchDragArmed = opts.onTouchDragArmed;
  // Read-only (Live) canvases keep tap-to-select / double-tap, but the
  // long-press node drag must never arm — structure is locked there.
  var isReadOnly = !!opts.isReadOnly;

  var [draggingId, setDraggingId] = useState(null);
  var [resizingId, setResizingId] = useState(null);
  var [touchDraggingId, setTouchDraggingId] = useState(null);

  // When the dragged node is a Tool Calling agent, its tool nodes (wired from its
  // bottom anchor) should move with it — they belong to the agent. Merge their
  // start positions into the existing `otherStarts` map (the same mechanism used
  // for multi-select drag), so updateDraggedNodePosition shifts them by the same
  // delta. No-op for non-agent nodes. `starts` may be null; returns a map or null.
  function withAgentToolStarts(draggedId, nodes, starts) {
    var nodeById = {};
    for (var i = 0; i < nodes.length; i += 1) {
      if (nodes[i] && nodes[i].id != null) nodeById[String(nodes[i].id)] = nodes[i];
    }
    var dragged = nodeById[String(draggedId)];
    if (!dragged || dragged.icon !== 'tool-agent') return starts;
    var ws = workflowStateRef.current || {};
    var connections = Array.isArray(ws.connections) ? ws.connections : [];
    var result = starts || {};
    var added = false;
    connections.forEach(function(c) {
      if (!c || String(c.from) !== String(draggedId) || String(c.fromAnchor || '') !== 'bottom') return;
      var toNode = nodeById[String(c.to)];
      if (!toNode || !toNode.position) return;
      if (String(c.to) === String(draggedId)) return;
      if (result[String(c.to)]) return;
      result[String(c.to)] = { left: toNode.position.left, top: toNode.position.top };
      added = true;
    });
    return (starts || added) ? result : null;
  }
  var dragRef = useRef(null);
  var resizeRef = useRef(null);
  var touchDragStartRef = useRef(null);
  var longPressTimerRef = useRef(null);

  function updateDraggedNodePosition(clientX, clientY) {
    if (!dragRef.current) return;
    var drag = dragRef.current;
    var panDeltaX = panPosRef.current.x - drag.startPanX;
    var panDeltaY = panPosRef.current.y - drag.startPanY;
    var deltaX = (clientX - drag.startX - panDeltaX) / zoomRef.current;
    var deltaY = (clientY - drag.startY - panDeltaY) / zoomRef.current;
    var otherStarts = drag.otherStarts;

    if (drag.isGroup) {
      setWorkflowState(function(prev) {
        var groups = Array.isArray(prev.groups) ? prev.groups : [];
        var nextLeft = Math.round(drag.startLeft + deltaX);
        var nextTop = Math.round(drag.startTop + deltaY);
        var movedNodes = prev.nodes;
        if (otherStarts) {
          movedNodes = (prev.nodes || []).map(function(node) {
            var os = otherStarts[node.id];
            if (!os) return node;
            return Object.assign({}, node, {
              position: { left: Math.round(os.left + deltaX), top: Math.round(os.top + deltaY) }
            });
          });
        }
        return Object.assign({}, prev, {
          nodes: movedNodes,
          groups: groups.map(function(g) {
            if (g.id !== drag.id) return g;
            return Object.assign({}, g, { position: { left: nextLeft, top: nextTop } });
          })
        });
      });
      return;
    }

    setWorkflowState(function(prev) {
      var nextNodes = prev.nodes.map(function(node) {
        if (node.id === drag.id) {
          return Object.assign({}, node, {
            position: { left: Math.round(drag.startLeft + deltaX), top: Math.round(drag.startTop + deltaY) }
          });
        }
        if (otherStarts && otherStarts[node.id]) {
          var os = otherStarts[node.id];
          return Object.assign({}, node, {
            position: { left: Math.round(os.left + deltaX), top: Math.round(os.top + deltaY) }
          });
        }
        return node;
      });

      var groups = Array.isArray(prev.groups) ? prev.groups : [];
      var movedIds = [drag.id];
      if (otherStarts) movedIds = movedIds.concat(Object.keys(otherStarts));
      var groupsChanged = false;
      var nextGroups = groups.map(function(g) {
        if (!g.nodeIds || !g.nodeIds.length) return g;
        var firstNodeId = g.nodeIds[0];
        if (movedIds.indexOf(firstNodeId) === -1) return g;
        var firstNode = null;
        for (var ni = 0; ni < nextNodes.length; ni++) {
          if (nextNodes[ni].id === firstNodeId) { firstNode = nextNodes[ni]; break; }
        }
        if (!firstNode || !firstNode.position) return g;
        groupsChanged = true;
        return Object.assign({}, g, { position: { left: firstNode.position.left, top: firstNode.position.top } });
      });

      return Object.assign({}, prev, { nodes: nextNodes, groups: groupsChanged ? nextGroups : groups });
    });
  }

  function onDragMove(event) {
    if (!dragRef.current) return;
    updateDraggedNodePosition(event.clientX, event.clientY);
  }

  function onDragTouchMove(event) {
    if (!dragRef.current || !event.touches.length) return;
    event.preventDefault();
    updateDraggedNodePosition(event.touches[0].clientX, event.touches[0].clientY);
  }

  function stopDragging() {
    dragRef.current = null;
    setDraggingId(null);
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', stopDragging);
    window.removeEventListener('touchmove', onDragTouchMove);
    window.removeEventListener('touchend', stopDragging);
  }

  function startDragging(id, event) {
    if (event.button !== 0) return;
    if (event.target.closest('button')) return;
    var nodes = nodesRef.current;
    var selIds = selectedNodeIdsRef.current;

    var currentNode = null;
    for (var i = 0; i < nodes.length; i += 1) {
      if (nodes[i].id === id) { currentNode = nodes[i]; break; }
    }
    if (!currentNode || !currentNode.position) return;

    var otherStarts = null;
    if (selIds[id] && Object.keys(selIds).length > 1) {
      otherStarts = {};
      for (var j = 0; j < nodes.length; j += 1) {
        var otherNode = nodes[j];
        if (otherNode.id !== id && selIds[otherNode.id] && otherNode.position) {
          otherStarts[otherNode.id] = { left: otherNode.position.left, top: otherNode.position.top };
        }
      }
    }
    // Drag a Tool Calling node → its tool nodes come along.
    otherStarts = withAgentToolStarts(id, nodes, otherStarts);

    event.preventDefault();
    dragRef.current = {
      id: id, startX: event.clientX, startY: event.clientY,
      startLeft: currentNode.position.left, startTop: currentNode.position.top,
      startPanX: panPosRef.current.x, startPanY: panPosRef.current.y,
      otherStarts: otherStarts
    };
    setDraggingId(id);
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', stopDragging);
  }

  function startDraggingTouch(id, event) {
    if (event.target.closest('button')) return;
    var nodes = nodesRef.current;
    var selIds = selectedNodeIdsRef.current;
    var currentNode = null;
    for (var i = 0; i < nodes.length; i += 1) {
      if (nodes[i].id === id) { currentNode = nodes[i]; break; }
    }
    if (!currentNode || !currentNode.position || !event.touches.length) return;
    var touchOtherStarts = null;
    if (selIds[id] && Object.keys(selIds).length > 1) {
      touchOtherStarts = {};
      for (var ti = 0; ti < nodes.length; ti += 1) {
        var tn = nodes[ti];
        if (tn.id !== id && selIds[tn.id] && tn.position) {
          touchOtherStarts[tn.id] = { left: tn.position.left, top: tn.position.top };
        }
      }
    }
    // Drag a Tool Calling node → its tool nodes come along.
    touchOtherStarts = withAgentToolStarts(id, nodes, touchOtherStarts);
    event.preventDefault();
    var touch = event.touches[0];
    dragRef.current = {
      id: id, startX: touch.clientX, startY: touch.clientY,
      startLeft: currentNode.position.left, startTop: currentNode.position.top,
      startPanX: panPosRef.current.x, startPanY: panPosRef.current.y,
      otherStarts: touchOtherStarts
    };
    setDraggingId(id);
    window.addEventListener('touchmove', onDragTouchMove, { passive: false });
    window.addEventListener('touchend', stopDragging);
  }

  function startGroupDrag(groupId, event) {
    if (event.button !== 0) return;
    if (event.target.closest('button')) return;
    var ws = workflowStateRef.current;
    var group = null;
    var groups = Array.isArray(ws.groups) ? ws.groups : [];
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].id === groupId) { group = groups[i]; break; }
    }
    if (!group || !group.position) return;
    event.preventDefault();
    var memberStarts = {};
    var groupNodeSet = {};
    (group.nodeIds || []).forEach(function(nid) { groupNodeSet[nid] = true; });
    (ws.nodes || []).forEach(function(node) {
      if (!groupNodeSet[node.id] || !node.position) return;
      memberStarts[node.id] = { left: node.position.left, top: node.position.top };
    });
    dragRef.current = {
      id: groupId, isGroup: true,
      startX: event.clientX, startY: event.clientY,
      startLeft: group.position.left, startTop: group.position.top,
      startPanX: panPosRef.current.x, startPanY: panPosRef.current.y,
      otherStarts: memberStarts
    };
    setDraggingId(groupId);
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', stopDragging);
  }

  // Resize
  function onResizeMove(event) {
    if (!resizeRef.current) return;
    var resize = resizeRef.current;
    var dx = (event.clientX - resize.startX) / zoomRef.current;
    var minWidth = 220, maxWidth = 820;
    var nextLeft = resize.startLeft;
    var nextWidth = resize.startWidth;
    if (resize.edge === 'right') {
      nextWidth = Math.max(minWidth, Math.min(maxWidth, resize.startWidth + dx));
    } else if (resize.edge === 'left') {
      nextWidth = Math.max(minWidth, Math.min(maxWidth, resize.startWidth - dx));
      nextLeft = resize.startLeft + (resize.startWidth - nextWidth);
    } else { return; }
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, {
        nodes: prev.nodes.map(function(node) {
          if (node.id !== resize.id) return node;
          return Object.assign({}, node, {
            position: { left: Math.round(nextLeft), top: Math.round(resize.startTop) },
            size: { width: Math.round(nextWidth) }
          });
        })
      });
    });
  }

  function onResizeTouchMove(event) {
    if (!resizeRef.current || !event.touches.length) return;
    event.preventDefault();
    var touch = event.touches[0];
    var resize = resizeRef.current;
    var dx = (touch.clientX - resize.startX) / zoomRef.current;
    var minWidth = 220, maxWidth = 820;
    var nextLeft = resize.startLeft;
    var nextWidth = resize.startWidth;
    if (resize.edge === 'right') {
      nextWidth = Math.max(minWidth, Math.min(maxWidth, resize.startWidth + dx));
    } else if (resize.edge === 'left') {
      nextWidth = Math.max(minWidth, Math.min(maxWidth, resize.startWidth - dx));
      nextLeft = resize.startLeft + (resize.startWidth - nextWidth);
    } else { return; }
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, {
        nodes: prev.nodes.map(function(node) {
          if (node.id !== resize.id) return node;
          return Object.assign({}, node, {
            position: { left: Math.round(nextLeft), top: Math.round(resize.startTop) },
            size: { width: Math.round(nextWidth) }
          });
        })
      });
    });
  }

  function stopResizing() {
    resizeRef.current = null;
    setResizingId(null);
    window.removeEventListener('mousemove', onResizeMove);
    window.removeEventListener('mouseup', stopResizing);
    window.removeEventListener('touchmove', onResizeTouchMove);
    window.removeEventListener('touchend', stopResizing);
    measureNodes();
  }

  function startNodeResize(node, edge, event) {
    if (edge !== 'left' && edge !== 'right') return;
    event.preventDefault();
    event.stopPropagation();
    if (!node || !node.position) return;
    var currentRect = getRectRef.current(node);
    resizeRef.current = {
      id: node.id, edge: edge,
      startX: event.clientX, startY: event.clientY,
      startLeft: node.position.left, startTop: node.position.top,
      startWidth: currentRect.width, startHeight: currentRect.height
    };
    setResizingId(node.id);
    window.addEventListener('mousemove', onResizeMove);
    window.addEventListener('mouseup', stopResizing);
  }

  function startNodeResizeTouch(node, edge, event) {
    if (edge !== 'left' && edge !== 'right') return;
    event.preventDefault();
    event.stopPropagation();
    if (!node || !node.position || !event.touches.length) return;
    var currentRect = getRectRef.current(node);
    var touch = event.touches[0];
    resizeRef.current = {
      id: node.id, edge: edge,
      startX: touch.clientX, startY: touch.clientY,
      startLeft: node.position.left, startTop: node.position.top,
      startWidth: currentRect.width, startHeight: currentRect.height
    };
    setResizingId(node.id);
    window.addEventListener('touchmove', onResizeTouchMove, { passive: false });
    window.addEventListener('touchend', stopResizing);
  }

  // Touch drag (long-press to drag): the node only starts following the finger
  // after the finger holds still on it for LONG_PRESS_MS (a vibrate confirms).
  // If the finger moves past MOVE_CANCEL_THRESHOLD before the hold completes,
  // it's treated as a canvas pan/scroll — the node is NOT dragged. A short tap
  // that lifts before the hold simply selects the node.
  var LONG_PRESS_MS = 400;
  var MOVE_CANCEL_THRESHOLD = 10;

  function clearLongPressTimer() {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }

  function cancelTouchDrag() {
    clearLongPressTimer();
    touchDragStartRef.current = null;
    setTouchDraggingId(null);
    window.removeEventListener('touchmove', onTouchDragMove);
    window.removeEventListener('touchend', onTouchDragEnd);
    window.removeEventListener('touchcancel', onTouchDragCancel);
  }

  function beginTouchDragForNode(start) {
    var currentNodes = workflowStateRef.current.nodes || [];
    var currentNode = null;
    for (var i = 0; i < currentNodes.length; i += 1) {
      if (currentNodes[i].id === start.id) { currentNode = currentNodes[i]; break; }
    }
    if (!currentNode || !currentNode.position) return false;
    // Take the touch away from the canvas pan that started alongside us.
    if (onTouchDragArmed) onTouchDragArmed();
    if (onLongPressSelect) onLongPressSelect(start.id);
    setTouchDraggingId(start.id);
    if (navigator.vibrate) navigator.vibrate(15);
    dragRef.current = {
      id: start.id, startX: start.startX, startY: start.startY,
      startLeft: currentNode.position.left, startTop: currentNode.position.top,
      startPanX: panPosRef.current.x, startPanY: panPosRef.current.y,
      // Drag a Tool Calling node → its tool nodes come along.
      otherStarts: withAgentToolStarts(start.id, currentNodes, null)
    };
    setDraggingId(start.id);
    return true;
  }

  function onTouchDragMove(event) {
    // Already dragging (long-press armed): keep the node glued to the finger.
    if (dragRef.current) { onDragTouchMove(event); return; }
    if (event.touches.length >= 2) { cancelTouchDrag(); return; }
    var start = touchDragStartRef.current;
    if (!start || !event.touches.length) return;
    var touch = event.touches[0];
    var dist = Math.hypot(touch.clientX - start.startX, touch.clientY - start.startY);
    // Moved before the long-press fired → user is panning/scrolling, not
    // dragging the node. Release so the canvas can handle the gesture.
    if (dist > MOVE_CANCEL_THRESHOLD) { cancelTouchDrag(); }
  }

  function onTouchDragEnd() {
    var start = touchDragStartRef.current;
    var wasDragging = !!dragRef.current;
    cancelTouchDrag();
    if (wasDragging) { stopDragging(); return; }
    // Quick tap that never moved → select this node, and abort the canvas pan
    // started by the same touch so its tap-handler doesn't clear the selection.
    if (start) {
      if (onTouchDragArmed) onTouchDragArmed();
      if (onLongPressSelect) onLongPressSelect(start.id);
      // Report the completed tap with its touchstart time so the canvas can
      // detect double-taps from physical touch timing (render latency between
      // taps must not eat the double-tap window).
      if (onNodeTap) onNodeTap(start.id, start.startedAt);
    }
  }

  // A cancelled touch (browser gesture, notification pull, palm rejection) is
  // not a tap: disarm fully so the pending long-press timer can't fire later
  // and arm a phantom drag that would swallow the user's next tap.
  function onTouchDragCancel() {
    var wasDragging = !!dragRef.current;
    cancelTouchDrag();
    if (wasDragging) stopDragging();
  }

  function startTouchDrag(id, event) {
    if (event.target.closest('button')) return;
    if (!event.touches.length) return;
    if (event.touches.length > 1) { cancelTouchDrag(); return; }
    // NOTE: intentionally NOT stopping propagation — the canvas's touchstart
    // also runs and arms a pan on the same touch. If the finger moves before
    // the long-press fires, that pan handles it (canvas pans). If the hold
    // completes, beginTouchDragForNode() cancels the pan and takes over.
    var touch = event.touches[0];
    touchDragStartRef.current = { id: id, startX: touch.clientX, startY: touch.clientY, startedAt: Date.now() };
    // Arm the drag only after a still hold. Until then the canvas keeps its
    // default touch behavior (so a tap-and-move pans instead of dragging).
    clearLongPressTimer();
    if (!isReadOnly) {
      longPressTimerRef.current = setTimeout(function() {
        longPressTimerRef.current = null;
        var s = touchDragStartRef.current;
        if (s && !beginTouchDragForNode(s)) cancelTouchDrag();
      }, LONG_PRESS_MS);
    }
    window.addEventListener('touchmove', onTouchDragMove, { passive: false });
    window.addEventListener('touchend', onTouchDragEnd);
    window.addEventListener('touchcancel', onTouchDragCancel);
  }

  // Cleanup
  useEffect(function() {
    return function() {
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('mouseup', stopDragging);
      window.removeEventListener('touchmove', onDragTouchMove);
      window.removeEventListener('touchend', stopDragging);
      window.removeEventListener('mousemove', onResizeMove);
      window.removeEventListener('mouseup', stopResizing);
      window.removeEventListener('touchmove', onResizeTouchMove);
      window.removeEventListener('touchend', stopResizing);
      window.removeEventListener('touchmove', onTouchDragMove);
      window.removeEventListener('touchend', onTouchDragEnd);
      window.removeEventListener('touchcancel', onTouchDragCancel);
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, []);

  return {
    draggingId: draggingId,
    resizingId: resizingId,
    touchDraggingId: touchDraggingId,
    dragRef: dragRef,
    startDragging: startDragging,
    startDraggingTouch: startDraggingTouch,
    startGroupDrag: startGroupDrag,
    stopDragging: stopDragging,
    startNodeResize: startNodeResize,
    startNodeResizeTouch: startNodeResizeTouch,
    startTouchDrag: startTouchDrag,
    cancelTouchDrag: cancelTouchDrag
  };
}

module.exports = useNodeDrag;
